import {
  Component,
  Injector,
  OnInit,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { AccountInfo } from '@azure/msal-browser';
import { filter } from 'rxjs/operators';

import { AuthService } from './core/services/auth.service';
import { MsalService } from './core/services/msal.service';
import { ContextService } from './core/services/context.service';
import { SessionService } from './core/services/session.service';
import { LoadingService } from './core/services/loading.service';
import { NotificationService } from './core/services/notification.service';

import { HeaderComponent } from './shared/components/header/header.component';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { LoadingComponent } from './shared/components/loading/loading.component';
import { ModalComponent } from './shared/components/modal/modal.component';
import { NotificationsComponent } from './shared/components/notifications/notifications.component';
import type { DashboardStore } from './views/dashboard/dashboard.store';

const BREADCRUMB_MAP: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/kpis': 'KPI overview',
  '/alerts': 'Alerts',
  '/hurricanes': 'Hurricanes',
  '/journal': 'Processing journal',
  '/data': 'Recollect data',
  '/forecasts': 'Forecasts',
  '/postprocessing': 'Postprocessing',
  '/imo-packages': 'IMO packages',
  '/help': 'Help',
  '/account': 'Account',
  '/config': 'Config',
  '/login': 'Sign in',
};

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'support_hub_sidebar_collapsed';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderComponent,
    SidebarComponent,
    LoadingComponent,
    ModalComponent,
    NotificationsComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  protected readonly authService = inject(AuthService);
  private readonly msalService = inject(MsalService);
  private readonly contextService = inject(ContextService);
  private readonly sessionService = inject(SessionService);
  private readonly loadingService = inject(LoadingService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  /** Loaded with the first sign-in; it keeps the header notifications current on every page. */
  private dashboardStore?: DashboardStore;

  sidebarOpen = false;
  sidebarCollapsed = this.readSidebarCollapsedPreference();
  breadcrumb: string | undefined;

  constructor() {
    // Every way in and out of a session sets or clears the access token, so the dashboard checks follow it.
    effect(() => {
      const signedIn = this.authService.isAuthenticated();
      untracked(() => (signedIn ? void this.startDashboardChecks() : this.dashboardStore?.stop()));
    });
  }

  async ngOnInit(): Promise<void> {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => {
      this.breadcrumb = BREADCRUMB_MAP[e.urlAfterRedirects] ?? undefined;
    });

    this.loadingService.begin();
    try {
      const redirectResult = await this.msalService.handleRedirectResult();
      if (redirectResult?.account) {
        this.setPrincipalFromMsalAccount(redirectResult.account);
      }

      if (!this.authService.principal()) {
        const cachedAccount = this.msalService.getAllAccounts()[0];
        if (cachedAccount) {
          this.setPrincipalFromMsalAccount(cachedAccount);
        }
      }

      if (!this.authService.principal()) {
        this.router.navigate(['/login']);
        return;
      }

      if (!this.authService.hasUsableAccessToken(120_000)) {
        await this.tryAutoFetchAccessToken();
      }

      if (this.authService.isAuthenticated()) {
        this.sessionService.restoreSession();
        void this.loadSupportingContext();
      }

      if (!this.authService.isAuthenticated()) {
        this.router.navigate(['/login']);
      }
    } catch (error) {
      console.error('Authentication initialization error:', error);
      this.authService.clearAccessToken();
      this.notificationService.warn(
        'Sign-in unavailable',
        'Microsoft access could not be confirmed. Sign in again to continue.'
      );
      this.router.navigate(['/login']);
    } finally {
      this.loadingService.end();
    }
  }

  private async tryAutoFetchAccessToken(): Promise<void> {
    try {
      const response = await this.msalService.getAccessToken();
      if (!response?.accessToken) return;

      this.authService.setAccessToken(response.accessToken, response.expiresIn);
    } catch (error) {
      console.warn('Auto-fetch API access token failed on startup:', error);
      // Not critical; login page will handle this.
    }
  }

  private async loadSupportingContext(): Promise<void> {
    const results = await Promise.allSettled([
      this.contextService.loadServiceMeta(),
      this.contextService.loadContext(),
    ]);

    const failures = results.filter(result => result.status === 'rejected');
    if (failures.length > 0) {
      console.warn('Supporting application context could not be loaded:', failures);
    }
  }

  /**
   * The store comes with the dashboard chunk, so its rules and chart code stay out of the initial bundle. Start and stop
   * are idempotent, and the store is started only if the session is still signed in once the chunk has loaded.
   */
  private async startDashboardChecks(): Promise<void> {
    try {
      const { DashboardStore } = await import('./views/dashboard/dashboard.store');
      this.dashboardStore = this.injector.get(DashboardStore);
      if (this.authService.isAuthenticated()) this.dashboardStore.start();
    } catch (error) {
      console.warn('Dashboard checks could not be started:', error);
    }
  }

  private setPrincipalFromMsalAccount(account: AccountInfo): void {
    this.authService.setPrincipal({
      name: account.name,
      username: account.username,
      localAccountId: account.localAccountId,
    });
  }

  signIn(): void {
    this.router.navigate(['/login']);
  }

  signOut(): void {
    this.msalService.invalidateAccessToken();
    this.authService.clear();
    this.contextService.resetContext();
    this.sessionService.resetSession();
    this.router.navigate(['/login']);
  }

  toggleSidebar(viewportWidth = window.innerWidth): void {
    if (viewportWidth >= 768) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      this.sidebarOpen = false;
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(this.sidebarCollapsed));
      return;
    }

    this.sidebarOpen = !this.sidebarOpen;
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
  }

  private readSidebarCollapsedPreference(): boolean {
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  }
}
